import React from "react"
import Table, { Row, Cell } from "../../ui/Table"
import TableHeaderRow from "../../ui/TableHeaderRow"
import TableHeader from "../../ui/TableHeader"
import Timestamp from "../../ui/Timestamp"
import PropTypes from "prop-types"
import RSOType from "../rsos/RSOType"
import IDLink from "../../ui/IDLink"
import { selectSchedulePriorities } from "../schedules/slice"
import { useSelector } from "react-redux"
import SortHeader from "../sorting/SortHeader"
import DropDownFilter from "../filters/DropDownFilter"
import { selectRSOTypes } from "../rsos/slice"
import RSOFilter from "../rsos/filters/RSOFilter"
import { selectAdvancedMode } from "../settings/slice"
import DateTimeFilter from "../filters/DateTimeFilter"
import IDCopy from "../../ui/IDCopy"
import { selectAstrositesById } from "../astrosites/slice"
import AstrositeName from "../astrosites/AstrositeName"
import RSOInfo from "../rsos/RSOInfo"

/**
 * ScheduledObservationsTable renders a given list of scheduled observations
 * @param schedules
 * @param filter If provided will be used to power the filters if not filters will not be shown
 * @param setFilter
 * @returns {JSX.Element}
 * @constructor
 */
const ScheduledObservationsTable = ({
  scheduledObservations = [],
  filter = {},
  setFilter = () => {},
  hideFilters = false,
}) => {
  // User setting to show more details such as id's
  const advancedModeState = useSelector(selectAdvancedMode)
  // if filters apply to advancedMode columns we have to show them
  const advancedModeOverride = filter.id_cnts ? true : false
  const advancedMode = advancedModeOverride
    ? advancedModeOverride
    : advancedModeState
  const rosTypes = useSelector(selectRSOTypes) // The various types of RSO
  const priorityNames = useSelector(selectSchedulePriorities) // The possible values for priority
  const astrositesByIds = useSelector(selectAstrositesById)

  return (
    <Table>
      <TableHeaderRow sticky>
        <TableHeader
          filterControl={
            (!hideFilters && (
              <DateTimeFilter
                filter={filter}
                setFilter={setFilter}
                active={filter.from || filter.to ? true : false}
              />
            )) ||
            undefined
          }
          sortControl={
            filter && (
              <SortHeader
                setFilter={setFilter}
                filter={filter}
                sortBy={"date"}
              />
            )
          }
        >
          Date & Time
        </TableHeader>
        <TableHeader
          filterControl={
            (!hideFilters && (
              <DropDownFilter
                values={Object.keys(astrositesByIds).map((a) => ({
                  name: a,
                  value: a,
                }))}
                filterByName="astrosite_id"
                filter={filter}
                setFilter={setFilter}
                active={filter.astrosite_id ? true : false}
              />
            )) ||
            undefined
          }
          sortControl={
            filter && (
              <SortHeader
                setFilter={setFilter}
                filter={filter}
                sortBy={"astrosite_id"}
              />
            )
          }
        >
          Astrosite
        </TableHeader>
        <TableHeader
          filterControl={
            (!hideFilters && (
              <RSOFilter
                filter={filter}
                setFilter={setFilter}
                textEntryDebounceMs={100}
                active={filter.object_id ? true : false}
              />
            )) ||
            undefined
          }
          sortControl={
            <SortHeader
              setFilter={setFilter}
              filter={filter}
              sortBy={"object_name"}
            />
          }
        >
          Object
        </TableHeader>
        <TableHeader
          filterControl={
            (!hideFilters && (
              <DropDownFilter
                values={rosTypes.map((rso) => {
                  return { name: rso, value: rso }
                })}
                filterByName="object_type"
                filter={filter}
                setFilter={setFilter}
                active={filter.object_type ? true : false}
              />
            )) ||
            undefined
          }
          sortControl={
            <SortHeader
              setFilter={setFilter}
              filter={filter}
              sortBy={"object_type"}
            />
          }
        >
          Object Type
        </TableHeader>
        <TableHeader
          filterControl={
            (!hideFilters && (
              <DropDownFilter
                values={Object.values(priorityNames).map((pn) => ({
                  name: `${pn.name} (${pn.id})`,
                  value: `${pn.id}`,
                }))}
                filterByName="priority"
                filter={filter}
                setFilter={setFilter}
                active={
                  filter.priority && filter.priority !== "" ? true : false
                }
              />
            )) ||
            undefined
          }
          sortControl={
            <SortHeader
              setFilter={setFilter}
              filter={filter}
              sortBy={"priority"}
            />
          }
        >
          Priority
        </TableHeader>
        <TableHeader
          filterControl={
            (!hideFilters && (
              <DropDownFilter
                values={[
                  {
                    name: "true",
                    value: "true",
                  },
                  {
                    name: "false",
                    value: "false",
                  },
                ]}
                filterByName="observed"
                filter={filter}
                setFilter={setFilter}
                active={
                  filter.observed && filter.observed !== "" ? true : false
                }
              />
            )) ||
            undefined
          }
        >
          Observed
        </TableHeader>
        {advancedMode && (
          <TableHeader
            sortControl={
              filter && (
                <SortHeader
                  setFilter={setFilter}
                  filter={filter}
                  sortBy={"id"}
                />
              )
            }
          >
            ID
          </TableHeader>
        )}
        {/*Actions*/}
        <TableHeader />
      </TableHeaderRow>
      {scheduledObservations && (
        <tbody>
          {scheduledObservations.map((so) => (
            <Row key={so.id} zebra>
              <Cell>
                <Timestamp utc={advancedMode} columnMode={true}>
                  {so.date}
                </Timestamp>
              </Cell>
              <Cell>
                <AstrositeName astrositeId={so.astrosite} />
              </Cell>
              <Cell>
                {so.satellite ? (
                  <RSOInfo
                    key={so.satellite.id}
                    id={so.satellite.id}
                    object_name={so.satellite.object_name}
                    object_id={so.satellite.object_id}
                  />
                ) : (
                  <span>-</span>
                )}
              </Cell>
              <Cell>
                {so.satellite ? (
                  <div>
                    <span key={so.satellite.id}>
                      <RSOType type={so.satellite.object_type} printName />
                    </span>
                  </div>
                ) : (
                  <span>-</span>
                )}
              </Cell>
              <Cell>
                {priorityNames[so.priority]
                  ? priorityNames[so.priority].name
                  : "-"}
              </Cell>
              {/*TODO add tick boxes here?*/}
              <Cell>
                {!so.recording_id ? (
                  "false"
                ) : (
                  <IDLink to={`/observations/${so.recording_id}`}>
                    {so.recording_id}
                  </IDLink>
                )}
              </Cell>
              {advancedMode && (
                <Cell>
                  <IDCopy truncateTo={20} id={so.id} showCopy={true} />
                </Cell>
              )}
            </Row>
          ))}
        </tbody>
      )}
    </Table>
  )
}

ScheduledObservationsTable.propTypes = {
  scheduledObservations: PropTypes.array, // the rsos to render
  filter: PropTypes.object, // If provided will be used to power the filters if not filters will not be shown
  setFilter: PropTypes.func, // If provided will be used to power the filters if not filters will not be shown
  hideFilters: PropTypes.bool, // Hides the filters
}

export default ScheduledObservationsTable
